import type { FC, ReactNode } from 'react'

import { Plus } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'
import { DOWNLOADS, MAILTO_VRAAG, external } from '@/constants/leesritme'

const LINK_CLASS = 'font-semibold text-ink underline decoration-ink/25 underline-offset-4 hover:decoration-ink'

interface FaqItem {
    question: string
    answer: ReactNode
}

const FAQ_ITEMS: Array<FaqItem> = [
    {
        question: "Hebben we dan een nieuwe methode nodig?",
        answer: (
            <p>
                Nee. Leesritme werkt naast je huidige methode en voegt maar één ding toe: elke dag tien minuten een rijke tekst, in de klas en thuis.
            </p>
        )
    },
    {
        question: "Kost het mijn leerkrachten extra werk?",
        answer: (
            <p>
                Nauwelijks. Teksten, vragen en antwoorden staan klaar en de app kijkt na. De themaweek en de thuistekst verschijnen vanzelf. Een extra huiswerktekst zet je in drie klikken klaar, en in het portaal zie je wie heeft gelezen.
            </p>
        )
    },
    {
        question: "Is Leesritme zelf onderzocht?",
        answer: (
            <p>
                De onderdelen zijn onderzocht, Leesritme zelf nog niet. Daarom hoort bij de pilot op elke school een meting vooraf en achteraf. Zo zien wij en de school of het werkt.
            </p>
        )
    },
    {
        question: "Onze methode voor begrijpend lezen stopt. Kan Leesritme helpen?",
        answer: (
            <>
                <p>
                    Volgens Malmberg zijn{' '}
                    <a href='https://www.malmberg.nl/basisonderwijs/methodes/lezen/leeslink.htm' target='_blank' rel='noopener noreferrer' className={LINK_CLASS}>Leeslink</a>
                    {' '}en{' '}
                    <a href='https://malmberg.nl/basisonderwijs/methodes/lezen/grip-op-lezen' target='_blank' rel='noopener noreferrer' className={LINK_CLASS}>Grip op lezen</a>
                    {' '}dit schooljaar niet meer beschikbaar. Leesritme is geen complete taalmethode, maar wel een dagelijkse basis voor begrijpend lezen: elke dag een rijke tekst, thema’s die kennis opbouwen en vragen die terugwijzen naar de tekst.
                </p>

                <p>
                    Het werkt naast je taalmethode, ook terwijl je een vervanger kiest. En je kunt het eerst gratis proberen.
                </p>
            </>
        )
    },
    {
        question: "Op welke apparaten werkt het?",
        answer: (
            <p>
                In de browser, op Chromebooks, iPads, Windows-laptops en telefoons. Er hoeft niets geïnstalleerd te worden, al kun je de app wel op het beginscherm zetten. Na het eerste gebruik opent hij ook offline, en het werk wordt verstuurd zodra er weer verbinding is.
            </p>
        )
    },
    {
        question: "Hoe loggen leerlingen in?",
        answer: (
            <p>
                Met een klascode, hun naam en een geheime code van vier cijfers, of met drie plaatjes. De leerkracht kiest per klas en print de inlogkaartjes uit het portaal. Thuis werkt dezelfde inlog. In de{' '}
                <a href={DOWNLOADS.startgids} className={LINK_CLASS}>startgids (pdf)</a>
                {' '}staat elke stap.
            </p>
        )
    },
    {
        question: "Wat doet Leesritme voor kinderen met dyslexie of zwakke lezers?",
        answer: (
            <>
                <p>
                    Elke tekst kan worden voorgelezen en de letters kunnen groter. Voorlezen kan tot drie keer, met woorden die meekleuren. In het portaal zet je een kind tot twee groepen lager of hoger.
                </p>

                <p>
                    Verder leest het kind dezelfde rijke tekst als de klas, met steun zoals echo-lezen via een koptelefoon, in plaats van een versimpelde versie. Zo werkt het aan hetzelfde weekdoel en praat het mee in de klas.
                </p>
            </>
        )
    },
    {
        question: "Wat gebeurt er met de gegevens van leerlingen?",
        answer: (
            <>
                <p>
                    We bewaren zo weinig mogelijk: de voornaam, de eerste letter van de achternaam, antwoorden en leesvoortgang. Geen e-mailadres en geen geboortedatum. Spraakherkenning draait op het apparaat zelf, dus er gaat geen geluid naar een server. Elke pilotschool tekent een verwerkersovereenkomst.
                </p>

                <p>
                    <a href={external('/privacy')} className={LINK_CLASS}>Lees alles over privacy en veiligheid</a>
                </p>
            </>
        )
    },
    {
        question: "Ik ben ouder. Kan mijn kind meedoen?",
        answer: (
            <p>
                Ja. Kies ‘Ouder’ in het aanmeldformulier. Je krijgt dan een code waarmee je een account maakt en je kind klaarzet. Je kind leest thuis, en jij volgt de voortgang in je eigen portaal. Liever dat de hele klas meedoet? Onder het formulier maak je een berichtje voor de school. Meer staat op de{' '}
                <a href={external('/ouders')} className={LINK_CLASS}>pagina voor ouders</a>.
            </p>
        )
    }
]

export const FaqSection: FC = (): ReactNode => {
    return (
        <section id='faq' className='py-20 sm:py-28'>
            <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <SectionHeading
                        eyebrow='Veelgestelde vragen'
                        title='Wat scholen ons vragen'
                        intro={(
                            <>
                                Staat je vraag er niet bij?{' '}
                                <a href={MAILTO_VRAAG} className={LINK_CLASS}>Mail ons</a>
                                , of lees verder{' '}
                                <a href={external('/directie')} className={LINK_CLASS}>voor directie</a>
                                {' '}of{' '}
                                <a href={external('/leerkrachten')} className={LINK_CLASS}>voor leerkrachten</a>.
                            </>
                        )}
                    />
                </div>

                <div className='faq divide-y divide-ink/10 rounded-[2rem] border border-ink/10 bg-card px-5 shadow-soft sm:px-8'>
                    {FAQ_ITEMS.map(item => (
                        <details key={item.question} className='group py-1'>
                            <summary className='flex cursor-pointer items-center justify-between gap-6 rounded-xl py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf'>
                                <span className='font-heading text-lg leading-snug font-semibold text-ink sm:text-xl'>
                                    {item.question}
                                </span>

                                <span className='grid size-9 shrink-0 place-items-center rounded-full bg-sand text-ink transition group-open:rotate-45 group-open:bg-leaf group-open:text-white'>
                                    <Plus className='size-4' aria-hidden='true' />
                                </span>
                            </summary>

                            <div className='grid gap-3 pr-2 pb-6 text-[1.0625rem] leading-relaxed text-muted-foreground sm:pr-14'>
                                {item.answer}
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}
